/**
 * ProtectedRoute Component - Phase 4 Authentication
 *
 * Wraps routes that require authentication and specific role levels
 * Redirects to login if not authenticated or insufficient permissions
 */

import React from 'react';
import { Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { isPathAllowedForRole, landingPathForRole } from '../lib/roleAccess';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: 'viewer' | 'operator' | 'manager' | 'admin';
}

export function ProtectedRoute({ children, requiredRole = 'viewer' }: ProtectedRouteProps) {
  const { user, profile, loading, hasRole, signOut, roles } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  // Show loading state while checking authentication
  if (loading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        background: 'var(--background-secondary)',
      }}>
        <div style={{
          textAlign: 'center',
          color: 'var(--text-secondary)',
        }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '3px solid var(--border-primary)',
            borderTopColor: 'var(--primary)',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px',
          }} />
          {t('common.loading')}
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Wait for profile to load before making any routing decisions.
  // There is a brief window after onAuthStateChange sets `user` but before
  // loadUserData finishes where profile is still null and loading is false.
  if (!profile) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        background: 'var(--background-secondary)',
      }}>
        <div style={{
          textAlign: 'center',
          color: 'var(--text-secondary)',
        }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '3px solid var(--border-primary)',
            borderTopColor: 'var(--primary)',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px',
          }} />
          {t('common.loading')}
        </div>
      </div>
    );
  }

  // Redirect to password setup if the user hasn't set a password yet
  // (skip if we're already on the setup-password page to avoid a loop)
  if (!profile.password_set && location.pathname !== '/setup-password') {
    return <Navigate to="/setup-password" replace />;
  }

  // Per-role path policy. equipment_check users are operationally narrow:
  // map them to viewer-level for generic gates but redirect away from
  // sections they shouldn't see when accessed directly. See lib/roleAccess.ts.
  const currentRole = roles[0]?.role;
  if (currentRole === 'equipment_check' && !isPathAllowedForRole(currentRole, location.pathname)) {
    return <Navigate to={landingPathForRole(currentRole)} replace />;
  }

  // Check if user has required role level
  if (!hasRole(requiredRole)) {
    const handleLogout = async () => {
      await signOut();
      navigate('/login', { replace: true });
    };

    const handleGoToDashboard = () => {
      navigate('/', { replace: true });
    };

    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        background: 'var(--background-secondary)',
      }}>
        <div style={{
          maxWidth: '500px',
          padding: 'var(--spacing-xl)',
          background: 'var(--background-primary)',
          borderRadius: 'var(--radius-md)',
          textAlign: 'center',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        }}>
          <div style={{
            width: '64px',
            height: '64px',
            margin: '0 auto var(--spacing-md)',
            borderRadius: '50%',
            background: 'var(--error-light)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '32px',
          }}>
            🚫
          </div>
          <h2 style={{
            marginBottom: 'var(--spacing-md)',
            color: 'var(--text-primary)',
            fontSize: '24px',
          }}>
            {t('protectedRoute.accessDenied')}
          </h2>
          <p style={{
            color: 'var(--text-secondary)',
            marginBottom: 'var(--spacing-lg)',
            lineHeight: '1.6',
          }}>
            {t('protectedRoute.noPermission', { role: requiredRole })}
          </p>
          <p style={{
            color: 'var(--text-secondary)',
            marginBottom: 'var(--spacing-xl)',
            fontSize: '14px',
          }}>
            {t('protectedRoute.contactAdmin')}
          </p>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--spacing-md)',
          }}>
            <button
              onClick={handleGoToDashboard}
              className="btn btn-primary"
              style={{ width: '100%' }}
            >
              {t('protectedRoute.goToDashboard')}
            </button>
            <button
              onClick={handleLogout}
              className="btn btn-secondary"
              style={{ width: '100%' }}
            >
              {t('protectedRoute.logoutAndRetry')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // User is authenticated and has required permissions
  return <>{children}</>;
}
